import React, { useState } from 'react'
import sortBy from 'lodash/sortBy'
import { ControlledTextInput, ControlledSelect, ControlledCheckbox } from 'transfix-form/components'
import { IconButton } from 'transfix-ui/components/IconButton'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box, Button, makeStyles, Typography } from 'transfix-ui/core'
import {
  IConfigurableBuilder,
  ITransactionSetTypes,
  IUpdateConfigurableBuilderInput,
  ICreateConfigurableBuilderInput,
} from 'types/graphqlTypes'
import { EditIcon } from 'transfix-icons'
import { useConfigurableBuilderForm } from '../hooks/useConfigurableBuilderForm'
import {
  useTransactionSets,
  encodeOptionValue as encodeTransactionSetOptionValue,
  decodeOptionValue as decodeTransactionSetOptionValue,
} from '../hooks/useTransactionSets'
import { IConfigurableBuilderSchema } from '../schema/ConfigurableBuilderSchema'

interface ILoadBuilderModalProps extends Omit<IModalBaseProps, 'modalType'> {
  builders: IConfigurableBuilder[]
  onCancel: () => void
  onCreateBuilder: (formParams: ICreateConfigurableBuilderInput) => void
  onEditBuilder: (formParams: IUpdateConfigurableBuilderInput) => void
  onSelectBuilder: (builder: IConfigurableBuilder | null) => void
}

const useLoadBuilderModalStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  selectBuilderButton: {
    marginLeft: theme.spacing(1),
  },
}))

export const LoadBuilderModal = ({
  builders,
  onCancel,
  onCreateBuilder,
  onEditBuilder,
  onSelectBuilder,
  open = true,
  ...props
}: ILoadBuilderModalProps) => {
  const [showForm, setShowForm] = useState<boolean>(false)
  const [currentBuilder, setCurrentBuilder] = useState<IConfigurableBuilder | null>(null)
  const classes = useLoadBuilderModalStyles()
  const methods = useConfigurableBuilderForm()
  const { control, errors, handleSubmit } = methods

  const { selectOptions: transactionSetSelectOptions } = useTransactionSets()

  const saveBuilder = async (formParams: IConfigurableBuilderSchema) => {
    const decoded = decodeTransactionSetOptionValue(formParams.transactionSetWithSubtype)

    const params = {
      name: formParams.name,
      transactionSet: decoded.transactionSet,
      transactionSetSubtype: decoded.transactionSetSubtype,
      isLive: formParams.isLive,
    }

    if (currentBuilder?.id) {
      onEditBuilder({ ...params, id: currentBuilder.id })
    } else {
      onCreateBuilder(params)
    }

    setShowForm(false)
    setCurrentBuilder(null)
  }
  const submitHandler = handleSubmit(saveBuilder)

  const createActions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        setShowForm(false)
        setCurrentBuilder(null)
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]
  const defaultActions: IModalBaseProps['actions'] = [
    {
      label: 'Close',
      action: () => {
        setCurrentBuilder(null)
        onCancel()
      },
      type: 'dismiss',
      dataTest: 'close-button',
    },
  ]

  const sortedBuilders = sortBy(builders, ['name'])

  return (
    <ModalBase
      dataTest='LoadBuilderModal'
      modalType={MODAL_TYPES.form}
      size={6}
      open={open}
      actions={showForm ? createActions : defaultActions}
      {...props}>
      {showForm ? (
        <Box>
          <Box className={classes.header}>
            <Typography variant='h3'>
              {currentBuilder?.id ? 'Edit' : 'Create'} Configurable Builder
            </Typography>
          </Box>
          <Box mt={2} mb={2}>
            <ControlledTextInput
              control={control}
              name='name'
              defaultValue={currentBuilder?.name || ''}
              TextInputProps={{
                required: true,
                label: 'Builder Name',
                placeholder: 'Name',
                error: !!errors?.name?.message,
              }}
            />
            <ControlledSelect
              control={control}
              defaultValue={encodeTransactionSetOptionValue(
                currentBuilder?.transactionSet || ITransactionSetTypes._990,
                currentBuilder?.transactionSetSubtype
              )}
              name='transactionSetWithSubtype'
              SelectProps={{
                required: true,
                label: 'EDI Transaction Set',
                options: transactionSetSelectOptions,
                large: true,
                dataTest: 'edi-message-type',
                error: !!errors?.transactionSetWithSubtype?.message,
              }}
            />
            Live:{' '}
            <ControlledCheckbox
              control={control}
              name='isLive'
              defaultValue={currentBuilder?.isLive}
            />
          </Box>
        </Box>
      ) : (
        <Box>
          <Box className={classes.header}>
            <Typography variant='h3'>Load Configurable Builder</Typography>
            <Button
              onClick={() => {
                setShowForm(true)
              }}>
              Create New Builder
            </Button>
          </Box>
          {sortedBuilders.map(item => (
            <Box
              mt={2}
              border='1px solid #d9d9d9'
              padding={2}
              key={item.id}
              display='flex'
              flexDirection='row'
              flexWrap='wrap'
              alignItems='center'
              justifyContent='space-between'>
              <Typography variant='h3'>{item.name}</Typography>
              <Box>
                <IconButton
                  Icon={EditIcon}
                  color='secondary'
                  onClick={() => {
                    setCurrentBuilder(item)
                    setShowForm(true)
                  }}
                  size='large'
                />
                <Button
                  className={classes.selectBuilderButton}
                  onClick={() => {
                    onSelectBuilder(item)
                    onCancel()
                  }}>
                  Select
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </ModalBase>
  )
}
